import { StatusIcon } from '../../../../../icons/SvgIcons';
import { useAppSelector } from '../../../../../hooks/redux';
import {
  AISystem,
  ComplianceStatus
} from '../../../../../types/AISystems.types';
import { getUserInitials } from '../../../../../utils/Initials.util';
import RiskClassificationComponent from '../RiskClassification/RiskClassification';
import { TruncatedText } from '../../../../../utils/TruncatedText';
import CheckboxItem from '../../../../ui/Checkbox/Checkbox';

interface TableRowProps {
  system: AISystem;
  onSelect: (id: string) => void;
  onDelete?: () => void;
  showAccount?: boolean;
  checked: boolean;
  onRowSelection: (id: string, checked: boolean) => void;
  selectionDisabled?: boolean;
}

export function TableRow({
  system,
  onSelect,
  showAccount,
  checked,
  onRowSelection,
  selectionDisabled = false
}: TableRowProps) {
  const { user } = useAppSelector((state) => state.auth);

  // Built by filter/join rather than interpolated: Owner declares both names
  // non-null, but a null from the API would print the word "undefined" here.
  const ownerName = [system.owner?.firstName, system.owner?.lastName]
    .filter(Boolean)
    .join(' ');

  const getComplianceLabel = (status: ComplianceStatus | null) => {
    if (!status) return 'N/A';
    switch (status) {
      case 'COMPLIANT':
        return 'Compliant';
      case 'COMPLIANT_RISK_CLASSIFIED':
        return 'Compliant (Risk-Classified)';
      case 'NON_COMPLIANT':
        return 'Non-Compliant';
      case 'ATTENTION_REQUIRED':
        return 'Attention Required';
      case 'INITIATED':
        return 'Initiated';
      case 'CLASSIFYING':
        return 'Classifying';
      case 'IN_PROGRESS':
        return 'In Progress';
      default:
        return status;
    }
  };

  const getComplianceTextColor = (status: ComplianceStatus | null) => {
    if (!status) return 'text-gray-550';
    switch (status) {
      case 'ATTENTION_REQUIRED':
        return 'text-rose-700';
      default:
        return 'text-gray-550';
    }
  };

  const formatEntityLabel = (entity: string | null) => {
    if (!entity) return '--';
    return entity
      .toLowerCase()
      .split('_')
      .map((word, index) =>
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
      )
      .join(' ');
  };

  return (
    <>
      <tr
        key={system.id}
        onClick={() => onSelect(system.id)}
        className="w-full flex flex-row cursor-pointer bg-white border-t border-gray-100"
      >
        <td
          className="min-w-10 flex items-center justify-center cursor-default"
          onClick={(event) => event.stopPropagation()}
        >
          <CheckboxItem
            value={system.id}
            label={`Select ${system.name}`}
            textClasses="sr-only"
            checked={checked}
            onChange={onRowSelection}
            disabled={selectionDisabled}
          />
        </td>
        <td className="min-w-56 w-full flex h-14 py-3 px-6 gap-1 text-sm font-medium items-center text-gray-900v2">
          <TruncatedText text={system.name} className="hover:underline" />
        </td>
        <td className="min-w-40 w-full flex h-14 py-3 px-6 gap-1 text-sm font-normal text-gray-550 items-center">
          <span className="truncate">
            {system.roleLabel || formatEntityLabel(system.entity)}
          </span>
        </td>
        <td
          className={`min-w-44 w-full flex h-14 py-3 px-6 gap-1 text-sm font-normal items-center ${getComplianceTextColor(system.complianceStatus)}`}
        >
          {system.complianceStatus && (
            <span className="flex items-center gap-2 truncate">
              <span>{StatusIcon(system.complianceStatus)}</span>
              <span
                className="truncate"
                title={getComplianceLabel(system.complianceStatus)}
              >
                {getComplianceLabel(system.complianceStatus)}
              </span>
            </span>
          )}
          {!system.complianceStatus && <span>N/A</span>}
        </td>
        <td className="min-w-44 w-full flex h-14 py-3 px-6 gap-1 text-sm font-normal leading-5 items-center text-gray-550">
          {system.riskClassification ? (
            <RiskClassificationComponent
              label={
                system.classificationLabel ||
                formatEntityLabel(system.riskClassification)
              }
              classificationCode={system.riskClassification}
              severity={system.classificationSeverity}
            />
          ) : (
            '--'
          )}
        </td>
        <td className="min-w-40 w-full flex h-14 py-3 px-6 gap-4 text-sm font-normal leading-5 items-center text-gray-550">
          {system.owner ? (
            <>
              <div className="min-w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center">
                <span className="text-xs text-purple-700">
                  {getUserInitials(ownerName)}
                </span>
              </div>
              <span className="truncate">
                {user?.email === system.owner.email ? 'me' : ownerName || '--'}
              </span>
            </>
          ) : (
            <span>--</span>
          )}
        </td>
        {showAccount && (
          <td className="min-w-40 w-full flex h-14 py-3 px-6 gap-1 text-sm font-normal leading-5 items-center text-gray-550">
            <span className="truncate">{system.account?.name || '--'}</span>
          </td>
        )}
        <td className="min-w-40 w-full flex h-14 py-3 px-6 gap-1 text-sm font-normal leading-5 items-center text-gray-550">
          <span className="truncate">{system?.organization || '--'}</span>
        </td>
      </tr>
    </>
  );
}
